import { Modal, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  pickLocalizedText,
  type LocalizedText,
} from "@heraion/shared";
import {
  ExercisePicker,
  useBookmarksStore,
  useExerciseGroupsStore,
  type LoggerExercisePickerProps,
} from "@heraion/ui";

import { apiFetch } from "./api";
import { exerciseCatalog } from "./logger/exercisesCache";
import { confirmViaWindow } from "./confirm";
import { PainDot, usePainRiskLabel } from "./PainDotWeb";
import { useThemePalette } from "./theme";

// web-rnw mount of the SHARED Add-Exercise picker (the composer's
// "＋ Aggiungi esercizi"). Same component tree the mobile logger uses; this
// wrapper only owns the web presentation shell (a plain RNW Modal — no
// SafeArea on the DOM) and the adapters: the localStorage catalog source,
// the lean picker stores and the locale resolver. No recency on
// web (the recent-sessions window is a mobile cache) → name order after
// favourites. PainDot wired 2026-08-17 (PainDotWeb.tsx) — painMap has been
// a web-rnw surface since the pain-map-picker lift, 2026-08-16. Prop type
// widened 2026-08-19 from the composer-only ComposerPickerProps to the full
// LoggerExercisePickerProps (same shape mobile's own thin mount already
// uses): hostDeps.ts already declared this component against the wider
// type, so single-pick mode (e.g. the Timers settings card) was one prop
// away, not a new mount.
export function ExercisePickerMount(props: LoggerExercisePickerProps) {
  const { dark, p } = useThemePalette();
  const { i18n } = useTranslation();
  const bookmarks = useBookmarksStore(apiFetch);
  const { groups, removeGroup } = useExerciseGroupsStore(apiFetch);
  const getExerciseName = (name: LocalizedText) =>
    pickLocalizedText(name, coerceLocale(i18n.language));

  return (
    <Modal
      visible={props.visible}
      animationType="slide"
      onRequestClose={props.onClose}
    >
      <View style={{ flex: 1, backgroundColor: p.bg }}>
        <ExercisePicker
          {...props}
          dark={dark}
          catalog={exerciseCatalog}
          bookmarks={bookmarks}
          templates={groups}
          onRemoveTemplate={removeGroup}
          // RW-25 — supplied HERE, next to the removeGroup it guards, because
          // this mount is what knows the platform. It also closes a live gap:
          // the composer has wired onRemoveTemplate all along, so deleting a
          // saved superset was reachable on the web and quietly did nothing
          // (the panel asked through react-native's Alert, inert on RNW).
          confirm={confirmViaWindow}
          getExerciseName={getExerciseName}
          PainDot={PainDot}
          usePainRiskLabel={usePainRiskLabel}
        />
      </View>
    </Modal>
  );
}
